/**
 * Hook for PostgreSQL backup operations using pg_dump.
 * Uses TanStack Query mutation for state management.
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import { pgsqlBackup, pgsqlCheckTools } from '../services/backupRestoreApi';
import { useBackupRestoreStore } from '../store/backupRestoreStore';
import type { PgsqlBackupRequest, PgsqlBackupResult } from '../types/backupRestore';
import { useAnalytics } from '../../../core/analytics';

type UsePostgresBackupOptions = {
  onSuccess?: (result: PgsqlBackupResult, operationId: string) => void;
  onError?: (error: Error) => void;
};

/**
 * Hook for backing up a PostgreSQL database using pg_dump.
 */
export function usePostgresBackup(options: UsePostgresBackupOptions = {}) {
  const { addOperation, updateOperation } = useBackupRestoreStore();
  const { track } = useAnalytics();

  const mutation = useMutation({
    mutationFn: async (request: PgsqlBackupRequest) => {
      // Track backup initiation
      track('feature:backup_restore', { engine: 'pgsql' });

      // Add operation to store (it generates id, status, startedAt)
      const operationId = addOperation({
        type: 'pgsql-backup',
        connectionId: request.connectionId,
        databaseName: request.databaseName,
        filePath: request.filePath || '',
      });

      try {
        // Update status to in progress
        updateOperation(operationId, { status: 'inProgress', message: 'Backup starting...' });

        const result = await pgsqlBackup(request);

        // Update operation status
        updateOperation(operationId, {
          status: result.success ? 'succeeded' : 'failed',
          completedAt: new Date().toISOString(),
          error: result.error,
        });

        return { result, operationId };
      } catch (error) {
        // Update operation status on error
        updateOperation(operationId, {
          status: 'failed',
          completedAt: new Date().toISOString(),
          error: error instanceof Error ? error.message : 'Unknown error',
        });
        throw error;
      }
    },
    onSuccess: ({ result, operationId }) => {
      options.onSuccess?.(result, operationId);
    },
    onError: (error: Error) => {
      options.onError?.(error);
    },
  });

  return {
    backupDatabase: (request: PgsqlBackupRequest) => mutation.mutate(request),
    isBackingUp: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}

/**
 * Hook for checking if pg_dump tools are available.
 * @param customBinPath Optional custom path to PostgreSQL bin directory
 */
export function usePgDumpAvailability(customBinPath?: string) {
  const query = useQuery({
    queryKey: ['pgsql-tools-availability', customBinPath],
    queryFn: () => pgsqlCheckTools(customBinPath),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: false,
  });

  return {
    isAvailable: query.data?.available ?? false,
    toolPath: query.data?.path,
    version: query.data?.version,
    error: query.data?.error,
    isChecking: query.isLoading,
    refetch: query.refetch,
  };
}
